import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

export interface Terminology {
  people: string;
  companies: string;
  deals: string;
}

/** Naive singularize: strips trailing "s"/"es" for display like "Add Contact" */
export function singularize(word: string): string {
  if (!word) return word;
  const lower = word.toLowerCase();
  if (lower === "people") return word[0] === "P" ? "Person" : "person";
  if (lower.endsWith("ies")) return word.slice(0, -3) + (word[word.length - 3] === 'I' ? 'Y' : 'y');
  if (lower.endsWith("ses") || lower.endsWith("ches") || lower.endsWith("shes") || lower.endsWith("xes")) return word.slice(0, -2);
  if (lower.endsWith("s") && !lower.endsWith("ss")) return word.slice(0, -1);
  return word;
}

/**
 * The English wording a workspace starts with. A workspace that never renamed
 * its objects is *not* customized, so these values mean "fall back to the
 * translated label" rather than "the user chose the word People".
 */
export const DEFAULT_TERMINOLOGY: Terminology = {
  people: "People",
  companies: "Companies",
  deals: "Deals",
};

function isDefaultTerminology(value: Terminology | null): boolean {
  if (!value) return true;
  return (
    value.people === DEFAULT_TERMINOLOGY.people &&
    value.companies === DEFAULT_TERMINOLOGY.companies &&
    value.deals === DEFAULT_TERMINOLOGY.deals
  );
}

interface TerminologyContextValue {
  /** Plural object labels, e.g. for navigation and page titles. */
  terminology: Terminology;
  /** Singular object labels, e.g. for "Add person". */
  singular: Terminology;
  /** True when the workspace renamed its objects during setup. */
  isCustomized: boolean;
  setTerminology: (t: Terminology | null) => void;
  setupComplete: boolean;
  setSetupComplete: (v: boolean) => void;
}

/**
 * Fallback for components rendered outside the provider. It cannot call hooks,
 * so it carries the English wording; the provider supplies translated labels.
 */
const TerminologyContext = createContext<TerminologyContextValue>({
  terminology: DEFAULT_TERMINOLOGY,
  singular: { people: "Person", companies: "Company", deals: "Deal" },
  isCustomized: false,
  setTerminology: () => {},
  setupComplete: false,
  setSetupComplete: () => {},
});

export function TerminologyProvider({ children }: { children: ReactNode }) {
  // null means "not customized" — the labels then follow the user's language.
  const [custom, setCustom] = useState<Terminology | null>(null);
  const [setupComplete, setSetupComplete] = useState(true);
  const { t } = useTranslation();

  // Setup saves terminology on completion whether or not the user edited it,
  // so a value equal to the English defaults is treated as "not customized".
  const setTerminology = useCallback((value: Terminology | null) => {
    setCustom(isDefaultTerminology(value) ? null : value);
  }, []);

  const terminology = useMemo<Terminology>(
    () =>
      custom ?? {
        people: t("nav.people"),
        companies: t("nav.companies"),
        deals: t("nav.deals"),
      },
    [custom, t],
  );

  const singular = useMemo<Terminology>(
    () =>
      custom
        ? {
            // A custom word is the user's own, so only the English heuristic applies.
            people: singularize(custom.people),
            companies: singularize(custom.companies),
            deals: singularize(custom.deals),
          }
        : {
            people: t("common.person"),
            companies: t("common.company"),
            deals: t("common.deal"),
          },
    [custom, t],
  );

  const value = useMemo<TerminologyContextValue>(
    () => ({
      terminology,
      singular,
      isCustomized: custom !== null,
      setTerminology,
      setupComplete,
      setSetupComplete,
    }),
    [terminology, singular, custom, setTerminology, setupComplete],
  );

  return <TerminologyContext.Provider value={value}>{children}</TerminologyContext.Provider>;
}

export const useTerminology = () => useContext(TerminologyContext);
